import { type CSSProperties, useRef, useState } from 'react';
import { LOCALES } from '@/i18n';
import { FLAGS, useNavModel, type NavItem } from '@/components/layout/useNavModel';
import RestaurantSwitcherSheet from '@/components/RestaurantSwitcherSheet';
import { useRestaurantOpenStatus } from '@/features/opening-hours/hooks/useRestaurantOpenStatus';

/**
 * iOS skin of the desktop/tablet navigation chrome: a floating, translucent
 * "glass bubble" sidebar (see `.app-desktop-sidebar` in styles.css). Rendered
 * only under `?theme=ios`. The Android (`md`) base renders `NavigationRail`
 * instead. Both are pure presentation over `useNavModel()`.
 */
export default function Sidebar() {
  const {
    t,
    email,
    restaurantName,
    collapsed,
    toggleSidebar,
    locale,
    currentLang,
    langOpen,
    setLangOpen,
    langRef,
    userOpen,
    setUserOpen,
    userRef,
    operationsItems,
    functionalityItems,
    activeItemPath,
    navigate,
    handleLogout,
    selectLocale,
    getInitials,
  } = useNavModel();

  const openStatus = useRestaurantOpenStatus();

  // The brand block doubles as the restaurant switcher trigger: it opens the
  // shared picker as a popover anchored to itself (same component Settings uses).
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const brandRef = useRef<HTMLButtonElement>(null);

  const renderItem = (item: NavItem) => {
    const active = !!item.path && item.path === activeItemPath;
    return (
      <button
        key={item.path ?? item.title}
        type="button"
        data-nav-path={item.path}
        className={`app-sidebar-item${active ? ' app-sidebar-item-active' : ''}`}
        onClick={() => (item.action ? item.action() : navigate(item.path!))}
        title={item.warning ? `${item.title}: ${item.warning}` : item.title}
      >
        <i
          className="f7-icons app-sidebar-item-icon"
          style={{ '--nav-icon-color': item.color } as CSSProperties}
        >
          {item.icon}
        </i>
        <span className="app-sidebar-item-label">{item.title}</span>
        {!!item.badge && <span className="app-sidebar-item-badge">{item.badge}</span>}
        {item.warning && (
          <i className="f7-icons app-sidebar-item-warning" aria-label={item.warning}>
            exclamationmark_triangle_fill
          </i>
        )}
      </button>
    );
  };

  return (
    <div className="app-sidebar">
      {/* Brand - also the active-restaurant switcher trigger */}
      <div className="app-sidebar-header">
        <button
          type="button"
          ref={brandRef}
          className={`app-sidebar-brand${switcherOpen ? ' app-sidebar-brand-open' : ''}`}
          onClick={() => setSwitcherOpen((o) => !o)}
          title={t.settings.switchRestaurant}
          aria-haspopup="menu"
          aria-expanded={switcherOpen}
        >
          <span className="app-sidebar-brand-text">
            <span className="app-sidebar-brand-name">{restaurantName}</span>
            {openStatus && (
              <span
                className={`restaurant-status app-sidebar-status ${openStatus.isOpen ? 'is-open' : 'is-closed'}`}
              >
                <span className="restaurant-status-dot" />
                {openStatus.label}
              </span>
            )}
          </span>
          <i className="f7-icons app-sidebar-brand-chevron">chevron_up_chevron_down</i>
        </button>
      </div>

      <RestaurantSwitcherSheet
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        anchorEl={brandRef.current}
      />

      {/* Operations: the live-shift surfaces (+ fast-access tools). */}
      <nav className="app-sidebar-nav">
        <span className="app-sidebar-section-label">{t.nav.operations}</span>
        {operationsItems.map(renderItem)}
      </nav>

      {/* Functionality: management + catalogue surfaces. */}
      <div className="app-sidebar-section">
        <span className="app-sidebar-section-label">{t.nav.functionality}</span>
        {functionalityItems.map(renderItem)}
      </div>

      {/* Spacer */}
      <div className="app-sidebar-spacer" />

      {/* User + account menu */}
      <div className="app-sidebar-footer">
        <div className="app-sidebar-user-wrap" ref={userRef}>
          {userOpen && (
            <div className="app-sidebar-user-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="app-sidebar-user-menu-item app-sidebar-user-menu-item-danger"
                onClick={() => {
                  setUserOpen(false);
                  handleLogout();
                }}
              >
                <i className="f7-icons">square_arrow_left</i>
                <span>{t.signOut}</span>
              </button>
            </div>
          )}

          <button
            type="button"
            className={`app-sidebar-user${userOpen ? ' app-sidebar-user-open' : ''}`}
            onClick={() => setUserOpen((o) => !o)}
            title={email ?? undefined}
            aria-haspopup="menu"
            aria-expanded={userOpen}
          >
            <div className="app-sidebar-avatar">{email ? getInitials(email) : 'U'}</div>
            <div className="app-sidebar-user-info">
              <span className="app-sidebar-user-name">{email ? email.split('@')[0] : '-'}</span>
              <span className="app-sidebar-user-email">{email ?? '-'}</span>
            </div>
            <i className="f7-icons app-sidebar-user-chevron">chevron_up_chevron_down</i>
          </button>
        </div>

        {/* Tools: collapse toggle (grows, left) + language chip (right), one row */}
        <div className="app-sidebar-tools">
          <button
            type="button"
            className="app-sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={collapsed ? t.nav.expand : t.nav.collapse}
          >
            <i className="f7-icons app-sidebar-item-icon">
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </i>
            <span className="app-sidebar-item-label">
              {collapsed ? t.nav.expand : t.nav.collapse}
            </span>
          </button>

          <div className="app-sidebar-lang" ref={langRef}>
            {langOpen && (
              <div className="app-sidebar-lang-menu" role="menu">
                {LOCALES.map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={l.value === locale}
                    className={`app-sidebar-lang-option${l.value === locale ? ' app-sidebar-lang-option-active' : ''}`}
                    onClick={() => selectLocale(l.value)}
                  >
                    {(() => {
                      const Flag = FLAGS[l.country];
                      return <Flag className="app-sidebar-flag" title={l.native} />;
                    })()}
                    <span className="app-sidebar-lang-option-label">{l.native}</span>
                    {l.value === locale && <i className="f7-icons">checkmark_alt</i>}
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className={`app-sidebar-tool-lang${langOpen ? ' app-sidebar-tool-lang-open' : ''}`}
              onClick={() => setLangOpen((o) => !o)}
              title={currentLang?.native}
              aria-haspopup="menu"
              aria-expanded={langOpen}
            >
              {currentLang &&
                (() => {
                  const Flag = FLAGS[currentLang.country];
                  return <Flag className="app-sidebar-flag" title={currentLang.native} />;
                })()}
              <span className="app-sidebar-lang-code">{locale.toUpperCase()}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
